import React, { useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { Upload, X, File, Image as ImageIcon, Video, FileText, Loader2, Download, Trash2, Eye } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { downloadFile } from '../lib/fileDownload';
import { openFilePicker } from '../lib/filePicker';

interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  uploaded_by: string;
  created_at: string;
}

interface QuoteFileUploadProps {
  quoteId: string | null;
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

export const QuoteFileUpload: React.FC<QuoteFileUploadProps> = ({
  quoteId,
  onAttachmentsChange,
  disabled = false
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (quoteId) {
      fetchAttachments();
    } else {
      setAttachments([]);
    }
  }, [quoteId]);

  const fetchAttachments = async () => {
    if (!quoteId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('service_request_quote_attachments')
        .select('*')
        .eq('quote_id', quoteId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setAttachments(data || []);
      onAttachmentsChange?.(data || []);
    } catch (err: any) {
      console.error('Error fetching attachments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" is too large. Maximum size is 200MB.`;
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    if (!quoteId) {
      setError('Quote ID is required');
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create a unique file path
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${quoteId}/${timestamp}_${sanitizedFileName}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('service-request-quote-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('service-request-quote-attachments')
        .getPublicUrl(filePath);

      // Insert metadata into database
      const { error: dbError } = await supabase
        .from('service_request_quote_attachments')
        .insert({
          quote_id: quoteId,
          file_url: filePath,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'application/octet-stream',
          uploaded_by: user.id
        });

      if (dbError) {
        // If database insert fails, delete the uploaded file
        await supabase.storage
          .from('service-request-quote-attachments')
          .remove([filePath]);
        throw dbError;
      }

      setUploadProgress(100);
      await fetchAttachments();
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      await uploadFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleMobileFilePick = async () => {
    if (Platform.OS !== 'web') {
      try {
        const files = await openFilePicker();
        if (files.length > 0) {
          for (const file of files) {
            await uploadFile(file);
          }
        }
      } catch (error: any) {
        console.error('Error picking files:', error);
        setError(error.message || 'Failed to pick files');
      }
    }
  };

  const deleteAttachment = async (attachment: Attachment) => {
    if (!confirm(`Are you sure you want to delete "${attachment.file_name}"?`)) {
      return;
    }

    try {
      // Delete from storage first
      const { error: storageError } = await supabase.storage
        .from('service-request-quote-attachments')
        .remove([attachment.file_url]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('service_request_quote_attachments')
        .delete()
        .eq('id', attachment.id);

      if (dbError) throw dbError;

      await fetchAttachments();
    } catch (err: any) {
      console.error('Error deleting attachment:', err);
      setError(err.message);
    }
  };

  const downloadAttachment = async (attachment: Attachment) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon className="w-5 h-5" />;
    if (fileType.startsWith('video/')) return <Video className="w-5 h-5" />;
    if (fileType === 'application/pdf') return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  const viewImage = (attachment: Attachment) => {
    const { data: { publicUrl } } = supabase.storage
      .from('service-request-quote-attachments')
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {!disabled && quoteId && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={Platform.OS === 'web' ? handleDrag : undefined}
          onDragLeave={Platform.OS === 'web' ? handleDrag : undefined}
          onDragOver={Platform.OS === 'web' ? handleDrag : undefined}
          onDrop={Platform.OS === 'web' ? handleDrop : undefined}
          onClick={() => {
            if (!uploading) {
              if (Platform.OS === 'web') {
                fileInputRef.current?.click();
              } else {
                handleMobileFilePick();
              }
            }
          }}
        >
          {Platform.OS === 'web' && (
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInput}
              className="hidden"
              disabled={uploading}
            />
          )}
          
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
              <p className="text-xs text-gray-600">Uploading... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
              <p className="text-xs text-gray-600">
                Drag and drop files here, or click to select
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Maximum file size: 200MB
              </p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 p-2 rounded-lg text-xs flex items-start">
          <X className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Attachments List */}
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : attachments.length > 0 ? (
        <div className="space-y-2">
          <h5 className="text-xs font-medium text-gray-700">
            Attachments ({attachments.length})
          </h5>
          <div className="space-y-1">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <div className="text-gray-500 flex-shrink-0">
                    {getFileIcon(attachment.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {attachment.file_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(attachment.file_size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  {isImageFile(attachment.file_type) && (
                    <button
                      onClick={() => viewImage(attachment)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Preview image"
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    onClick={() => downloadAttachment(attachment)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Download"
                  >
                    <Download className="w-3 h-3" />
                  </button>
                  {!disabled && (
                    <button
                      onClick={() => deleteAttachment(attachment)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : quoteId ? (
        <p className="text-xs text-gray-500 text-center py-2">
          No attachments yet
        </p>
      ) : null}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteFileUpload;

